import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import {
  Loader2,
  Plus,
  Trash2,
  CalendarClock,
  Send,
  CheckCircle2,
  Clock,
  Layers,
} from "lucide-react";
import { API, buildHeaders, formatDeadline } from "./shared";

const STATUS_BADGE = {
  draft: { bg: "#f1f5f9", fg: "#475569", label: "Draft" },
  scheduled: { bg: "#dbeafe", fg: "#1d4ed8", label: "Scheduled" },
  sent: { bg: "#dcfce7", fg: "#15803d", label: "Sent" },
};

export default function WavesTab({ session, campaign, onChanged }) {
  const [waves, setWaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const headers = useMemo(() => buildHeaders(session), [session]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/campaigns/${campaign.id}/waves`, { headers });
      setWaves(r.data || []);
    } catch { toast.error("Failed to load waves"); }
    finally { setLoading(false); }
  }, [campaign.id, headers]);

  useEffect(() => { load(); }, [load]);

  const addWave = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/api/admin/campaigns/${campaign.id}/waves`, {}, { headers });
      toast.success("Wave added");
      load();
      onChanged && onChanged();
    } catch { toast.error("Could not add wave"); }
    finally { setCreating(false); }
  };

  const deleteWave = async (w) => {
    if (!window.confirm(`Delete Wave ${w.wave_number} (${w.name})? This will not delete past sent records.`)) return;
    try {
      await axios.delete(`${API}/api/admin/campaigns/${campaign.id}/waves/${w.id}`, { headers });
      toast.success("Wave deleted");
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14" data-testid="waves-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#94a3b8]" />
      </div>
    );
  }

  return (
    <div data-testid="waves-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#475569]" />
          <h2 className="text-[15px] font-bold text-[#0f172a]">Waves — {waves.length}</h2>
        </div>
        <button
          type="button"
          onClick={addWave}
          disabled={creating}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-[12.5px] font-semibold border border-dashed text-[#475569] hover:bg-[#f8fafc] disabled:opacity-50"
          style={{ borderColor: "#cbd5e1" }}
          data-testid="waves-add-btn"
        >
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Add Wave
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="waves-grid">
        {waves.map((w) => {
          const b = STATUS_BADGE[w.status] || STATUS_BADGE.draft;
          return (
            <div
              key={w.id}
              className="rounded-xl border bg-white p-4 flex flex-col gap-2"
              style={{ borderColor: "#e5e7eb" }}
              data-testid={`wave-card-${w.wave_number}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10.5px] font-bold tracking-[0.10em] uppercase text-[#94a3b8]">Wave {w.wave_number}</div>
                  <div className="text-[14px] font-bold text-[#0f172a] truncate">{w.name}</div>
                </div>
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold tracking-[0.04em] uppercase"
                  style={{ background: b.bg, color: b.fg }}
                  data-testid={`wave-status-${w.wave_number}`}
                >
                  {b.label}
                </span>
              </div>
              <div className="text-[12px] text-[#475569] line-clamp-1" title={w.subject}>
                <span className="font-semibold">Subject:</span> {w.subject || <em className="text-[#94a3b8]">—</em>}
              </div>
              <div className="text-[12px] text-[#475569] flex items-center gap-3">
                {w.scheduled_at ? (
                  <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {formatDeadline(String(w.scheduled_at).slice(0, 10))}</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#94a3b8]"><Clock className="h-3 w-3" /> Unscheduled</span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Send className="h-3 w-3" /> {w.sent_count || 0} sent
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-[#94a3b8] inline-flex items-center gap-1">
                  {w.status === "sent" && <CheckCircle2 className="h-3 w-3 text-[#16a34a]" />}
                  {w.calendly_url ? "Calendly linked" : "No Calendly link"}
                </span>
                <button
                  type="button"
                  onClick={() => deleteWave(w)}
                  className="p-1.5 rounded-md text-[#b91c1c] hover:bg-[#fee2e2]"
                  title="Delete wave"
                  data-testid={`wave-delete-${w.wave_number}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-md px-3 py-2 text-[11.5px] flex items-center gap-1.5"
        style={{ background: "rgba(59,79,228,0.06)", color: "#3B4FE4" }}>
        Use the <strong>Compose</strong> tab to edit a wave's subject, body, and Calendly link.
      </div>
    </div>
  );
}
